import { z } from "zod";
import { createValidateZipCode } from "@lending/utils/validators";
import type { ReactLocalization } from "@fluent/react";
import type { AddressFormData, ApplicantDetailsAddressType } from "@lending/types";
import type { UseFormSetError } from "react-hook-form";

const ADDRESS_FIELDS = [
  "street_address",
  "street_address_2",
  "city",
  "region_code",
  "postal_code",
] as const;

type AddressPath = "residential_address" | "mailing_address";
type AddressField = (typeof ADDRESS_FIELDS)[number];
type AddressFormPath = `${AddressPath}.${AddressField}`;

function isAddressPath(value: string): value is AddressPath {
  return value === "residential_address" || value === "mailing_address";
}

function isAddressField(value: string): value is AddressField {
  return ADDRESS_FIELDS.includes(value as AddressField);
}

function setAddressErrors(
  address: ApplicantDetailsAddressType,
  path: AddressPath,
  ctx: z.RefinementCtx,
  requiredMessage: string
) {
  if (!address.street_address) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: requiredMessage,
      path: [path, "street_address"],
    });
  }
  if (!address.city) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: requiredMessage, path: [path, "city"] });
  }
  if (!address.region_code) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: requiredMessage,
      path: [path, "region_code"],
    });
  }
}

function createAddressSchema(l10n: ReactLocalization, askMailingAddress: boolean) {
  const requiredMessage = l10n.getString("error-required", null, "Required");
  const addressSchema = z.object({
    street_address: z.string().min(1, { message: requiredMessage }),
    street_address_2: z.string().default(""),
    city: z.string().min(1, { message: requiredMessage }),
    region_code: z.string().min(1, { message: requiredMessage }),
    postal_code: createValidateZipCode(l10n),
  });
  const partialAddressSchema = z.object({
    street_address: z.string().default(""),
    street_address_2: z.string().default(""),
    city: z.string().default(""),
    region_code: z.string().default(""),
    postal_code: z.string().default(""),
  });

  return z
    .object({
      residential_address: addressSchema,
      mailing_address: partialAddressSchema,
      hasSameMailingAddress: z.boolean(),
    })
    .superRefine((value, ctx) => {
      if (askMailingAddress && !value.hasSameMailingAddress) {
        setAddressErrors(value.mailing_address, "mailing_address", ctx, requiredMessage);

        const zipCodeValidation = createValidateZipCode(l10n).safeParse(
          value.mailing_address.postal_code
        );
        if (!zipCodeValidation.success) {
          zipCodeValidation.error.issues.forEach((issue) => {
            ctx.addIssue({
              ...issue,
              path: ["mailing_address", "postal_code"],
            });
          });
        }
      }
    });
}

function applyAddressErrors(
  error: Record<string, unknown>,
  setError: UseFormSetError<AddressFormData>,
  showErrorToast: (message: string) => void,
  requiredMessage: string
) {
  let hasFieldErrors = false;
  let hasToastErrors = false;

  Object.entries(error).forEach(([key, value]) => {
    if (!isAddressPath(key)) {
      return;
    }

    if (typeof value === "string") {
      hasToastErrors = true;
      showErrorToast(value);
      return;
    }

    if (Array.isArray(value) && value.length > 0) {
      hasToastErrors = true;
      showErrorToast(String(value[0]));
      return;
    }

    if (value && typeof value === "object") {
      Object.entries(value).forEach(([addressKey, addressValue]) => {
        if (!isAddressField(addressKey)) {
          return;
        }

        hasFieldErrors = true;
        setError(`${key}.${addressKey}` as AddressFormPath, {
          message:
            typeof addressValue === "string"
              ? addressValue
              : Array.isArray(addressValue) && addressValue.length > 0
                ? String(addressValue[0])
                : requiredMessage,
        });
      });
    }
  });

  return { hasFieldErrors, hasToastErrors };
}

export { applyAddressErrors, createAddressSchema };
